import PropTypes from "prop-types"
import React, { useState, useRef, useEffect } from "react"
import Button from "components/ui/Button"
import { trackEvent } from "lib/googleTagManager"
import submitHubspotForm from "lib/submitHubspotForm"
import * as css from "./PartnerContactForm.module.css"

function PartnerContactForm({
  formId,
  trackSubmit,
  thankYouMessage = "Thanks! We'll be in touch soon.",
  sourcePage,
}) {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    message: "",
  })
  const formRef = useRef(null)
  const hasTrackedView = useRef(false)

  // Track when form enters viewport (form viewed)
  useEffect(() => {
    const formElement = formRef.current
    if (!formElement || hasTrackedView.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true
          trackEvent("contactSalesFormViewed", {
            formId,
            sourcePage: sourcePage || "",
            pageSlug:
              typeof window !== "undefined" ? window.location.pathname : "",
            pageName: typeof document !== "undefined" ? document.title : "",
          })
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(formElement)
    return () => observer.disconnect()
  }, [formId, sourcePage])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const data = {
        ...formData,
        ...(sourcePage && { source_page: sourcePage }),
      }

      const response = await submitHubspotForm(formId, data)

      if (response.ok) {
        setSubmitted(true)
        if (trackSubmit) {
          trackEvent("formSubmission", {
            formSubmissionEventName: trackSubmit,
          })
        }
      } else {
        setError("Something went wrong. Please try again.")
      }
    } catch (_err) {
      setError("Something went wrong. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return <p className={css.thankYou}>{thankYouMessage}</p>
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className={css.form}>
      <div className={css.nameRow}>
        <div className={css.field}>
          <label htmlFor="firstname" className={css.label}>
            First Name <span className={css.required}>*</span>
          </label>
          <input
            type="text"
            id="firstname"
            name="firstname"
            value={formData.firstname}
            onChange={handleChange}
            required
            className={css.input}
          />
        </div>

        <div className={css.field}>
          <label htmlFor="lastname" className={css.label}>
            Last Name <span className={css.required}>*</span>
          </label>
          <input
            type="text"
            id="lastname"
            name="lastname"
            value={formData.lastname}
            onChange={handleChange}
            required
            className={css.input}
          />
        </div>
      </div>

      <div className={css.field}>
        <label htmlFor="email" className={css.label}>
          Email <span className={css.required}>*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className={css.input}
        />
      </div>

      <div className={css.field}>
        <label htmlFor="message" className={css.label}>
          Message
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          className={css.textarea}
          placeholder="Tell us about your project or questions..."
        />
      </div>

      {error && <p className={css.error}>{error}</p>}

      <Button
        type="submit"
        color="lush"
        size="medium"
        disabled={submitting}
        className={css.submitButton}
      >
        {submitting ? "Sending..." : "Submit"}
      </Button>
    </form>
  )
}

PartnerContactForm.propTypes = {
  formId: PropTypes.string.isRequired,
  trackSubmit: PropTypes.string,
  thankYouMessage: PropTypes.string,
  sourcePage: PropTypes.string,
}

export default PartnerContactForm
